// TABS
export const TABS_XSD = {
  XSD: 'XSD',
  CERTIFICATE_TYPES: 'Certificados',
  CERTIFICATE_CONFIGURATION: 'CERTIFICATE_CONFIGURATION',
};

// COMPLETE XSD DATA TYPE CATALOG
export const XSD_TYPE_CATALOG = {
  string: ['string', 'normalizedString', 'token'],
  numeric: [
    'integer', 'int', 'long', 'short', 'byte',
    'decimal', 'float', 'double',
    'nonNegativeInteger', 'positiveInteger',
    'negativeInteger', 'nonPositiveInteger',
  ],
  dateTime: [
    'date', 'time', 'dateTime', 'duration',
    'gYear', 'gYearMonth', 'gMonth', 'gMonthDay', 'gDay',
  ],
  boolean: ['boolean'],
  binary:  ['base64Binary', 'hexBinary'],
  reference: ['anyURI', 'QName', 'NOTATION'],
  xmlIdentity: ['ID', 'IDREF', 'IDREFS', 'ENTITY', 'ENTITIES', 'NMTOKEN', 'NMTOKENS'],
};

/** Human-readable labels by group */
export const XSD_TYPE_GROUP_LABELS = {
  string:      'Cadena de texto',
  numeric:     'Numérico',
  dateTime:    'Fecha y hora',
  boolean:     'Booleano',
  binary:      'Binario',
  reference:   'Referencia / URI',
  xmlIdentity: 'Identidad XML',
};

/**
 * Grouped options for react-select or a similar component.
 * Format: [ { label: 'Group', options: [{value, label}] } ]
 */
export const typeDataOptions = Object.entries(XSD_TYPE_CATALOG).map(([group, types]) => ({
  label: XSD_TYPE_GROUP_LABELS[group] || group,
  options: types.map((t) => ({ value: t, label: t })),
}));

// ─── Helper: find the group for a type ──────────────────────────
export const getTypeGroup = (type) => {
  if (!type) return null;
  for (const [group, types] of Object.entries(XSD_TYPE_CATALOG)) {
    if (types.includes(type)) return group;
  }
  return null;
};

// FACETS APPLICABLE BY TYPE GROUP
export const FACETS_BY_GROUP = {
  string:      ['minLength', 'maxLength', 'length', 'pattern', 'enumeration', 'whiteSpace'],
  numeric:     ['minInclusive', 'maxInclusive', 'minExclusive', 'maxExclusive', 'totalDigits', 'fractionDigits', 'pattern', 'enumeration'],
  dateTime:    ['minInclusive', 'maxInclusive', 'minExclusive', 'maxExclusive', 'pattern', 'enumeration'],
  boolean:     ['pattern'],
  binary:      ['minLength', 'maxLength', 'length'],
  reference:   ['minLength', 'maxLength', 'length', 'pattern', 'enumeration', 'whiteSpace'],
  xmlIdentity: ['minLength', 'maxLength', 'length', 'pattern', 'enumeration', 'whiteSpace'],
};

/** Return the facets applicable to an XSD type. */
export const getApplicableFacets = (type) => {
  const group = getTypeGroup(type);
  return group ? (FACETS_BY_GROUP[group] || []) : [];
};

/** Descriptive labels for each facet. */
export const FACET_LABELS = {
  minLength:      { label: 'Longitud mínima', placeholder: 'ej. 5', type: 'integer' },
  maxLength:      { label: 'Longitud máxima', placeholder: 'ej. 100', type: 'integer' },
  length:         { label: 'Longitud exacta', placeholder: 'ej. 10', type: 'integer' },
  pattern:        { label: 'Patrón (regex XSD)', placeholder: 'ej. [A-Z]{2}[0-9]{6}', type: 'text' },
  enumeration:    { label: 'Valores permitidos', placeholder: 'Agrega un valor', type: 'enum' },
  minInclusive:   { label: 'Mínimo inclusive', placeholder: 'ej. 0', type: 'text' },
  maxInclusive:   { label: 'Máximo inclusive', placeholder: 'ej. 999', type: 'text' },
  minExclusive:   { label: 'Mínimo exclusive', placeholder: 'ej. -1', type: 'text' },
  maxExclusive:   { label: 'Máximo exclusive', placeholder: 'ej. 1000', type: 'text' },
  totalDigits:    { label: 'Dígitos totales', placeholder: 'ej. 10', type: 'integer' },
  fractionDigits: { label: 'Dígitos decimales', placeholder: 'ej. 2', type: 'integer' },
  whiteSpace:     { label: 'Manejo de espacios', placeholder: '', type: 'enum',
                    options: ['preserve', 'replace', 'collapse'] },
};

// ENUMS AND OPTIONS
export const NODE_KIND_OPTIONS = [
  { value: 'ATTRIBUTE', label: 'Atributo (xs:attribute)' },
  { value: 'ELEMENT',   label: 'Elemento (xs:element)' },
];

export const COMPOSITOR_OPTIONS = [
  { value: 'sequence', label: 'sequence — hijos en orden declarado' },
  { value: 'choice',   label: 'choice — solo un hijo puede aparecer' },
  { value: 'all',      label: 'all — todos los hijos, cualquier orden' },
];

export const CONTENT_MODEL_OPTIONS = [
  { value: 'default',        label: 'Estándar (compositor + atributos)' },
  { value: 'simpleContent',  label: 'simpleContent — texto + atributos' },
  { value: 'complexContent', label: 'complexContent — hijos con herencia' },
  { value: 'empty',          label: 'Vacío — solo atributos' },
  { value: 'mixed',          label: 'Mixed — texto e hijos intercalados' },
];

export const INHERITANCE_OPTIONS = [
  { value: 'extension',    label: 'extension — agrega campos al tipo base' },
  { value: 'restriction',  label: 'restriction — limita el tipo base' },
];

export const USE_OPTIONS = [
  { value: 'optional',   label: 'optional' },
  { value: 'required',   label: 'required' },
  { value: 'prohibited', label: 'prohibited' },
];

export const PROCESS_CONTENTS_OPTIONS = [
  { value: 'strict', label: 'strict — debe validar contra el esquema' },
  { value: 'lax',    label: 'lax — valida si hay esquema disponible' },
  { value: 'skip',   label: 'skip — no valida' },
];

export const CONSTRAINT_KIND_OPTIONS = [
  { value: 'unique', label: 'unique — valores únicos (permite nulos)' },
  { value: 'key',    label: 'key — valores únicos y no nulos' },
  { value: 'keyref', label: 'keyref — referencia a un key definido' },
];

export const FORM_DEFAULT_OPTIONS = [
  { value: 'unqualified', label: 'unqualified (default)' },
  { value: 'qualified',   label: 'qualified' },
];

// FACTORIES — createEmpty*
export const createEmptyFacets = () => ({
  minLength:      null,
  maxLength:      null,
  length:         null,
  pattern:        '',
  enumValues:     [],
  minInclusive:   '',
  maxInclusive:   '',
  minExclusive:   '',
  maxExclusive:   '',
  totalDigits:    null,
  fractionDigits: null,
  whiteSpace:     '',
});

export const createEmptyConstraint = () => ({
  name:     '',
  kind:     'unique',
  selector: '',
  fields:   [''],
  refer:    '',
});

/**
 * Empty field with all properties from the new model.
 * Backward compatible: includes legacy regex/required properties.
 */
export const createEmptyField = () => ({
  itemName:       '',
  nodeKind:       'ATTRIBUTE',
  type:           '',
  // Cardinality (ELEMENT)
  minOccurs:      1,
  maxOccurs:      '1',
  nillable:       false,
  // Attribute
  use:            'optional',
  defaultValue:   '',
  fixedValue:     '',
  // Facets
  minLength:      null,
  maxLength:      null,
  length:         null,
  pattern:        '',
  enumValues:     [],
  minInclusive:   '',
  maxInclusive:   '',
  minExclusive:   '',
  maxExclusive:   '',
  totalDigits:    null,
  fractionDigits: null,
  whiteSpace:     '',
  // Legacy
  regex:          '',
  regexPreset:    '',
  regexPresetId:  '',
  required:       false,
  stringValidate: '',
});

/**
 * Empty node with all properties from the new model.
 */
export const createEmptyNode = () => ({
  name:                 '',
  // Cardinality
  minOccurs:            1,
  maxOccurs:            '1',
  // Content compositor.
  compositor:           'sequence',
  // Content model
  contentModel:         'default',
  inheritance:          '',
  baseType:             '',
  // Extensibility
  abstractElement:      false,
  substitutionGroup:    '',
  nillable:             false,
  // Wildcards
  hasAny:               false,
  anyProcessContents:   'strict',
  hasAnyAttribute:      false,
  anyAttrProcessContents: 'strict',
  // Referential integrity
  constraints:          [],
  // Fields and children
  items:                [],
  childrens:            [],
});

// CROSS-FIELD FACET VALIDATION (client-side)
/**
 * Validate cross-field facet rules.
 * Returns { valid: true } or { valid: false, message: string }.
 */
export const validateFieldFacets = (field) => {
  // length excludes minLength/maxLength
  if (field.length != null && (field.minLength != null || field.maxLength != null)) {
    return { valid: false, message: `Campo "${field.itemName}": 'length' no puede coexistir con 'minLength' o 'maxLength'.` };
  }
  // minLength <= maxLength
  if (field.minLength != null && field.maxLength != null && field.minLength > field.maxLength) {
    return { valid: false, message: `Campo "${field.itemName}": minLength (${field.minLength}) no puede ser mayor que maxLength (${field.maxLength}).` };
  }
  // Inclusive and Exclusive are mutually exclusive.
  if (field.minInclusive && field.minExclusive) {
    return { valid: false, message: `Campo "${field.itemName}": 'minInclusive' y 'minExclusive' son mutuamente excluyentes.` };
  }
  if (field.maxInclusive && field.maxExclusive) {
    return { valid: false, message: `Campo "${field.itemName}": 'maxInclusive' y 'maxExclusive' son mutuamente excluyentes.` };
  }
  // fractionDigits <= totalDigits
  if (field.fractionDigits != null && field.totalDigits != null && field.fractionDigits > field.totalDigits) {
    return { valid: false, message: `Campo "${field.itemName}": fractionDigits (${field.fractionDigits}) no puede ser mayor que totalDigits (${field.totalDigits}).` };
  }
  return { valid: true };
};

/**
 * Validate minOccurs <= maxOccurs cardinality.
 */
export const validateCardinality = (minOccurs, maxOccurs, label) => {
  if (maxOccurs === 'unbounded') return { valid: true };
  const max = parseInt(maxOccurs, 10);
  if (isNaN(max)) return { valid: false, message: `${label}: maxOccurs "${maxOccurs}" no es válido.` };
  if (minOccurs > max) {
    return { valid: false, message: `${label}: minOccurs (${minOccurs}) no puede ser mayor que maxOccurs (${max}).` };
  }
  return { valid: true };
};

// USER HELP GUIDE
export const XSD_HELP_SECTIONS = [
  {
    id: 'tipos-dato',
    title: 'Tipos de dato',
    iconType: 'font',
    color: 'blue',
    items: [
      { term: 'string / normalizedString / token', desc: 'Texto libre. token colapsa espacios automáticamente.' },
      { term: 'integer / int / decimal / float', desc: 'Números enteros o decimales con distintas precisiones.' },
      { term: 'date / time / dateTime', desc: 'Fechas y tiempos en formato ISO 8601 (YYYY-MM-DD, HH:MM:SS).' },
      { term: 'boolean', desc: 'Valores true/false o 1/0.' },
      { term: 'anyURI', desc: 'URLs o URIs absolutas/relativas.' },
      { term: 'ID / IDREF / IDREFS', desc: 'Identificadores únicos en el documento XML.' },
    ],
  },
  {
    id: 'cardinalidad',
    title: 'Cardinalidad',
    iconType: 'layer-group',
    color: 'violet',
    items: [
      { term: 'minOccurs=0', desc: 'El elemento es opcional.' },
      { term: 'maxOccurs="unbounded"', desc: 'El elemento puede repetirse sin límite.' },
      { term: 'sequence', desc: 'Los hijos deben aparecer exactamente en el orden declarado.' },
      { term: 'choice', desc: 'Solo uno de los hijos puede aparecer (o varios si maxOccurs > 1).' },
      { term: 'all', desc: 'Todos los hijos deben aparecer en cualquier orden. En XSD 1.0 cada hijo requiere maxOccurs=1.' },
    ],
  },
  {
    id: 'facets',
    title: 'Facets de restricción',
    iconType: 'lock',
    color: 'amber',
    items: [
      { term: 'minLength / maxLength', desc: 'Rango de longitud de cadena. No combinables con length.' },
      { term: 'length', desc: 'Longitud exacta. Excluye minLength y maxLength.' },
      { term: 'pattern', desc: 'Expresión regular XSD (sintaxis similar a PCRE, sin ^ ni $).' },
      { term: 'enumeration', desc: 'Lista de valores literales permitidos. Se puede combinar con pattern.' },
      { term: 'minInclusive / maxInclusive', desc: 'Rango numérico/fecha con extremos incluidos.' },
      { term: 'minExclusive / maxExclusive', desc: 'Rango numérico/fecha con extremos excluidos. Mutuamente excluyentes con Inclusive.' },
      { term: 'totalDigits', desc: 'Número total de dígitos (solo decimal y derivados).' },
      { term: 'fractionDigits', desc: 'Número máximo de decimales. Debe ser ≤ totalDigits.' },
      { term: 'whiteSpace', desc: 'preserve: conserva todo. replace: cambia \\t\\n\\r por espacio. collapse: colapsa espacios múltiples.' },
    ],
  },
  {
    id: 'atributos',
    title: 'Atributos',
    iconType: 'tag',
    color: 'green',
    items: [
      { term: 'use: required', desc: 'El atributo debe aparecer siempre. No puede tener valor default.' },
      { term: 'use: optional', desc: 'El atributo es opcional (valor por defecto de use).' },
      { term: 'use: prohibited', desc: 'El atributo no puede aparecer en la instancia.' },
      { term: 'default', desc: 'Valor asumido cuando el atributo no aparece (solo si use=optional).' },
      { term: 'fixed', desc: 'Si el atributo aparece, su valor debe ser exactamente este.' },
    ],
  },
  {
    id: 'tipos-complejos',
    title: 'Modelos de contenido',
    iconType: 'cube',
    color: 'teal',
    items: [
      { term: 'Estándar', desc: 'Compositor de hijos elemento + atributos. El caso más común.' },
      { term: 'simpleContent', desc: 'Elemento con texto plano y atributos. Sin hijos elemento.' },
      { term: 'complexContent', desc: 'Hijos elemento heredando de un tipo base via extension o restriction.' },
      { term: 'Vacío', desc: 'Solo atributos, sin contenido de texto ni hijos.' },
      { term: 'Mixed', desc: 'Texto libre e hijos elemento mezclados.' },
    ],
  },
  {
    id: 'integridad',
    title: 'Integridad referencial',
    iconType: 'key',
    color: 'rose',
    items: [
      { term: 'unique', desc: 'El valor (o combinación de campos) no se repite en el scope del selector. Permite nulos.' },
      { term: 'key', desc: 'Como unique, pero además el valor no puede ser nulo.' },
      { term: 'keyref', desc: 'El valor debe existir como key en otro punto del documento (llave foránea XML).' },
      { term: 'selector', desc: 'XPath que delimita el scope de la constraint (ej. Facturas/Factura).' },
      { term: 'field', desc: 'XPath que identifica el campo clave (ej. @id o NumeroFactura).' },
    ],
  },
  {
    id: 'extensibilidad',
    title: 'Extensibilidad',
    iconType: 'project-diagram',
    color: 'indigo',
    items: [
      { term: 'abstract', desc: 'El tipo/elemento no puede usarse directamente; solo como base de herencia.' },
      { term: 'substitutionGroup', desc: 'Permite que este elemento sustituya a otro en la instancia XML.' },
      { term: 'nillable', desc: 'Permite xsi:nil="true" en la instancia para indicar valor nulo explícito.' },
      { term: 'xs:any', desc: 'Acepta cualquier elemento no declarado. processContents controla si valida.' },
      { term: 'xs:anyAttribute', desc: 'Acepta cualquier atributo no declarado.' },
    ],
  },
  {
    id: 'fuera-alcance',
    title: 'Fuera del alcance de XSD',
    iconType: 'warning',
    color: 'gray',
    items: [
      { term: 'Reglas condicionales cruzadas', desc: 'Si campo A = X, entonces B es obligatorio. Requiere Schematron o validación custom.' },
      { term: 'Validaciones aritméticas', desc: 'total = precio × cantidad. XSD no puede expresar operaciones matemáticas.' },
      { term: 'Reglas de negocio contextuales', desc: 'Dependientes de base de datos o fecha actual. Requieren motor externo.' },
    ],
  },
];

// COMPLETE PAYLOAD SERIALIZATION (CRUD)
export const serializeFieldForPayload = (field) => ({
  itemName: field.itemName?.trim() || '',
  nodeKind: field.nodeKind || 'ELEMENT',
  type: field.type || 'string',
  minOccurs: field.nodeKind === 'ELEMENT' ? Number(field.minOccurs ?? 1) : 1,
  maxOccurs: field.nodeKind === 'ELEMENT' ? String(field.maxOccurs ?? '1') : '1',
  use: field.nodeKind === 'ATTRIBUTE' ? (field.use || 'optional') : 'optional',
  defaultValue: field.defaultValue || '',
  fixedValue: field.fixedValue || '',
  nillable: Boolean(field.nillable),
  length: field.length != null && field.length !== '' ? Number(field.length) : null,
  minLength: field.minLength != null && field.minLength !== '' ? Number(field.minLength) : null,
  maxLength: field.maxLength != null && field.maxLength !== '' ? Number(field.maxLength) : null,
  pattern: field.pattern || field.regex || '',
  regex: field.regex || field.pattern || '',
  regexPresetId: field.regexPresetId || '',
  enumValues: Array.isArray(field.enumValues) ? field.enumValues : [],
  minInclusive: field.minInclusive || '',
  maxInclusive: field.maxInclusive || '',
  minExclusive: field.minExclusive || '',
  maxExclusive: field.maxExclusive || '',
  totalDigits: field.totalDigits != null && field.totalDigits !== '' ? Number(field.totalDigits) : null,
  fractionDigits: field.fractionDigits != null && field.fractionDigits !== '' ? Number(field.fractionDigits) : null,
  whiteSpace: field.whiteSpace || '',
  required: field.nodeKind === 'ATTRIBUTE' ? field.use === 'required' : Boolean(field.required),
});

export const serializeNodeForPayload = (node) => ({
  name: node.name?.trim() || '',
  minOccurs: Number(node.minOccurs ?? 1),
  maxOccurs: String(node.maxOccurs ?? '1'),
  compositor: node.compositor || 'sequence',
  contentModel: node.contentModel || 'default',
  inheritance: node.inheritance || '',
  baseType: node.baseType || '',
  abstractElement: Boolean(node.abstractElement),
  substitutionGroup: node.substitutionGroup || '',
  nillable: Boolean(node.nillable),
  hasAny: Boolean(node.hasAny),
  anyProcessContents: node.anyProcessContents || 'strict',
  hasAnyAttribute: Boolean(node.hasAnyAttribute),
  anyAttrProcessContents: node.anyAttrProcessContents || 'strict',
  constraints: Array.isArray(node.constraints) ? node.constraints.map((c) => ({
    name: c.name?.trim() || '',
    kind: c.kind || 'unique',
    selector: c.selector?.trim() || '',
    fields: Array.isArray(c.fields) ? c.fields.map((f) => (typeof f === 'string' ? f.trim() : String(f))) : [],
    refer: c.refer?.trim() || '',
  })) : [],
  items: Array.isArray(node.items) ? node.items.map(serializeFieldForPayload) : [],
  childrens: (Array.isArray(node.childrens) ? node.childrens : (Array.isArray(node.children) ? node.children : [])).map(serializeNodeForPayload),
});

export const serializeVersionForPayload = (versionDraft) => ({
  version: (versionDraft.version || '').trim(),
  current: Boolean(versionDraft.current),
  status: versionDraft.status || 'PUBLISHED',
  effectiveDate: versionDraft.effectiveDate ? versionDraft.effectiveDate : null,
  targetNamespace: versionDraft.targetNamespace || '',
  elementFormDefault: versionDraft.elementFormDefault || 'unqualified',
  attributeFormDefault: versionDraft.attributeFormDefault || 'unqualified',
  structure: (versionDraft.structure || []).map(serializeNodeForPayload),
});

export const normalizeField = (field) => {
  if (!field) return createEmptyField();
  return {
    itemName: field.itemName || field.campo || '',
    nodeKind: field.nodeKind || (field.isAttribute ? 'ATTRIBUTE' : 'ELEMENT'),
    type: field.type || 'string',
    minOccurs: field.minOccurs ?? 1,
    maxOccurs: field.maxOccurs ?? '1',
    use: field.use || (field.required ? 'required' : 'optional'),
    defaultValue: field.defaultValue || '',
    fixedValue: field.fixedValue || '',
    nillable: Boolean(field.nillable),
    length: field.length != null ? field.length : null,
    minLength: field.minLength != null ? field.minLength : null,
    maxLength: field.maxLength != null ? field.maxLength : null,
    pattern: field.pattern || field.regex || '',
    regex: field.regex || field.pattern || '',
    regexPresetId: field.regexPresetId || '',
    regexPreset: field.regexPreset || '',
    enumValues: Array.isArray(field.enumValues) ? [...field.enumValues] : [],
    minInclusive: field.minInclusive || '',
    maxInclusive: field.maxInclusive || '',
    minExclusive: field.minExclusive || '',
    maxExclusive: field.maxExclusive || '',
    totalDigits: field.totalDigits != null ? field.totalDigits : null,
    fractionDigits: field.fractionDigits != null ? field.fractionDigits : null,
    whiteSpace: field.whiteSpace || '',
    required: field.nodeKind === 'ATTRIBUTE' ? field.use === 'required' : Boolean(field.required),
    stringValidate: field.stringValidate || '',
  };
};

export const normalizeNodeTree = (node) => {
  if (!node) return createEmptyNode();
  const rawItems = Array.isArray(node.items) ? node.items : (Array.isArray(node.campos) ? node.campos : []);
  const rawChildren = Array.isArray(node.childrens)
    ? node.childrens
    : (Array.isArray(node.children)
      ? node.children
      : (Array.isArray(node.nodos)
        ? node.nodos
        : (Array.isArray(node.hijos) ? node.hijos : [])));
  const normalizedChildren = rawChildren.map(normalizeNodeTree);
  return {
    name: node.name || '',
    minOccurs: node.minOccurs ?? 1,
    maxOccurs: String(node.maxOccurs ?? '1'),
    compositor: node.compositor || 'sequence',
    contentModel: node.contentModel || 'default',
    inheritance: node.inheritance || '',
    baseType: node.baseType || '',
    abstractElement: Boolean(node.abstractElement),
    substitutionGroup: node.substitutionGroup || '',
    nillable: Boolean(node.nillable),
    hasAny: Boolean(node.hasAny),
    anyProcessContents: node.anyProcessContents || 'strict',
    hasAnyAttribute: Boolean(node.hasAnyAttribute),
    anyAttrProcessContents: node.anyAttrProcessContents || 'strict',
    constraints: Array.isArray(node.constraints) ? [...node.constraints] : [],
    items: rawItems.map(normalizeField),
    childrens: normalizedChildren,
    children: normalizedChildren,
  };
};

export const normalizeVersion = (versionData) => {
  const rawStructure = versionData?.structure || versionData?.estructura || versionData?.nodos || [];
  return {
    version: versionData?.version || '',
    current: Boolean(versionData?.current),
    status: versionData?.status || 'PUBLISHED',
    effectiveDate: versionData?.effectiveDate ? String(versionData.effectiveDate).substring(0, 16) : '',
    targetNamespace: versionData?.targetNamespace || '',
    elementFormDefault: versionData?.elementFormDefault || 'unqualified',
    attributeFormDefault: versionData?.attributeFormDefault || 'unqualified',
    structure: rawStructure.length > 0 ? rawStructure.map(normalizeNodeTree) : [createEmptyNode()],
  };
};

// TEST EXAMPLES
export const XSD_TEST_EXAMPLES = [
  {
    id: 'titulacion',
    name: 'Certificado de Titulación (Completo)',
    description: 'Esquema completo con atributos obligatorios, datos del titular, carrera con enumeraciones, promedio con 2 decimales y firma electrónica en base64.',
    targetNamespace: 'https://certificacion.queretaro.gob.mx/schema/titulacion',
    elementFormDefault: 'qualified',
    attributeFormDefault: 'unqualified',
    structure: [
      {
        name: 'CertificadoTitulacion',
        minOccurs: 1,
        maxOccurs: '1',
        compositor: 'sequence',
        contentModel: 'default',
        constraints: [
          {
            name: 'unicidadFolio',
            kind: 'unique',
            selector: '.',
            fields: ['@folio'],
            refer: '',
          },
        ],
        items: [
          {
            itemName: 'folio',
            nodeKind: 'ATTRIBUTE',
            type: 'string',
            use: 'required',
            pattern: '^[A-Z]{3}-[0-9]{8}-[0-9]{4}$',
            regex: '^[A-Z]{3}-[0-9]{8}-[0-9]{4}$',
          },
          {
            itemName: 'fechaEmision',
            nodeKind: 'ATTRIBUTE',
            type: 'date',
            use: 'required',
          },
          {
            itemName: 'version',
            nodeKind: 'ATTRIBUTE',
            type: 'string',
            use: 'required',
            fixedValue: '1.0.0',
          },
        ],
        childrens: [
          {
            name: 'Titular',
            minOccurs: 1,
            maxOccurs: '1',
            compositor: 'sequence',
            contentModel: 'default',
            items: [
              {
                itemName: 'curp',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
                pattern: '^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[A-Z0-9]{2}$',
                regex: '^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[A-Z0-9]{2}$',
              },
              {
                itemName: 'nombre',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
                minLength: 2,
                maxLength: 100,
              },
              {
                itemName: 'primerApellido',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
                minLength: 2,
                maxLength: 100,
              },
              {
                itemName: 'segundoApellido',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 0,
                maxOccurs: '1',
                minLength: 2,
                maxLength: 100,
              },
            ],
            childrens: [],
          },
          {
            name: 'Titulacion',
            minOccurs: 1,
            maxOccurs: '1',
            compositor: 'sequence',
            contentModel: 'default',
            items: [
              {
                itemName: 'carrera',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
                minLength: 3,
                maxLength: 200,
              },
              {
                itemName: 'modalidad',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
                enumValues: ['TESIS', 'TESINA', 'EXAMEN_GENERAL', 'PROMEDIO', 'OTRO'],
              },
              {
                itemName: 'fechaTitulacion',
                nodeKind: 'ELEMENT',
                type: 'date',
                minOccurs: 1,
                maxOccurs: '1',
              },
              {
                itemName: 'promedio',
                nodeKind: 'ELEMENT',
                type: 'decimal',
                minOccurs: 1,
                maxOccurs: '1',
                fractionDigits: 2,
                minInclusive: '0.00',
                maxInclusive: '10.00',
              },
            ],
            childrens: [],
          },
          {
            name: 'Institucion',
            minOccurs: 1,
            maxOccurs: '1',
            compositor: 'sequence',
            contentModel: 'default',
            items: [
              {
                itemName: 'clave',
                nodeKind: 'ATTRIBUTE',
                type: 'string',
                use: 'required',
                pattern: '^[0-9]{8}$',
                regex: '^[0-9]{8}$',
              },
              {
                itemName: 'nombre',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
              },
              {
                itemName: 'rvoe',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 0,
                maxOccurs: '1',
              },
            ],
            childrens: [],
          },
          {
            name: 'FirmaElectronica',
            minOccurs: 0,
            maxOccurs: '1',
            compositor: 'sequence',
            contentModel: 'default',
            items: [
              {
                itemName: 'sello',
                nodeKind: 'ELEMENT',
                type: 'base64Binary',
                minOccurs: 1,
                maxOccurs: '1',
              },
              {
                itemName: 'certificado',
                nodeKind: 'ELEMENT',
                type: 'base64Binary',
                minOccurs: 1,
                maxOccurs: '1',
              },
              {
                itemName: 'noCertificado',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
                pattern: '^[0-9]{20}$',
                regex: '^[0-9]{20}$',
              },
            ],
            childrens: [],
          },
        ],
      },
    ],
  },
  {
    id: 'calificaciones',
    name: 'Certificado de Calificaciones (Lista Dinámica / Unbounded)',
    description: 'Demuestra listas repetitivas con maxOccurs="unbounded", tipos numéricos con restricciones de rango y validación de estatus.',
    targetNamespace: 'https://certificacion.queretaro.gob.mx/schema/calificaciones',
    elementFormDefault: 'qualified',
    attributeFormDefault: 'unqualified',
    structure: [
      {
        name: 'CertificadoCalificaciones',
        minOccurs: 1,
        maxOccurs: '1',
        compositor: 'sequence',
        contentModel: 'default',
        items: [
          {
            itemName: 'folio',
            nodeKind: 'ATTRIBUTE',
            type: 'string',
            use: 'required',
            pattern: '^[A-Z]{3}-[0-9]{8}$',
            regex: '^[A-Z]{3}-[0-9]{8}$',
          },
          {
            itemName: 'cicloEscolar',
            nodeKind: 'ATTRIBUTE',
            type: 'string',
            use: 'required',
            pattern: '^[0-9]{4}-[0-9]{4}$',
            regex: '^[0-9]{4}-[0-9]{4}$',
          },
        ],
        childrens: [
          {
            name: 'Alumno',
            minOccurs: 1,
            maxOccurs: '1',
            compositor: 'sequence',
            contentModel: 'default',
            items: [
              {
                itemName: 'matricula',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
                pattern: '^[A-Z]{3}-[0-9]{6}$',
                regex: '^[A-Z]{3}-[0-9]{6}$',
              },
              {
                itemName: 'curp',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
                pattern: '^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[A-Z0-9]{2}$',
                regex: '^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[A-Z0-9]{2}$',
              },
              {
                itemName: 'nombreCompleto',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
                minLength: 3,
                maxLength: 150,
              },
            ],
            childrens: [],
          },
          {
            name: 'Asignatura',
            minOccurs: 1,
            maxOccurs: 'unbounded',
            compositor: 'sequence',
            contentModel: 'default',
            items: [
              {
                itemName: 'clave',
                nodeKind: 'ATTRIBUTE',
                type: 'string',
                use: 'required',
                minLength: 4,
                maxLength: 10,
              },
              {
                itemName: 'nombre',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
                minLength: 3,
                maxLength: 120,
              },
              {
                itemName: 'creditos',
                nodeKind: 'ELEMENT',
                type: 'integer',
                minOccurs: 1,
                maxOccurs: '1',
                minInclusive: '1',
                maxInclusive: '40',
              },
              {
                itemName: 'calificacion',
                nodeKind: 'ELEMENT',
                type: 'decimal',
                minOccurs: 1,
                maxOccurs: '1',
                fractionDigits: 1,
                minInclusive: '0.0',
                maxInclusive: '10.0',
              },
              {
                itemName: 'estatus',
                nodeKind: 'ELEMENT',
                type: 'string',
                minOccurs: 1,
                maxOccurs: '1',
                enumValues: ['APROBADA', 'REPROBADA', 'EQUIVALENCIA'],
              },
            ],
            childrens: [],
          },
        ],
      },
    ],
  },
  {
    id: 'eleccion-choice',
    name: 'Certificado con Elección Alternativa (xs:choice)',
    description: 'Demuestra el compositor xs:choice donde solo una de las modalidades alternativas puede aparecer en el documento XML.',
    targetNamespace: 'https://certificacion.queretaro.gob.mx/schema/acreditacion',
    elementFormDefault: 'qualified',
    attributeFormDefault: 'unqualified',
    structure: [
      {
        name: 'ConstanciaAcreditacion',
        minOccurs: 1,
        maxOccurs: '1',
        compositor: 'sequence',
        contentModel: 'default',
        items: [
          {
            itemName: 'id',
            nodeKind: 'ATTRIBUTE',
            type: 'string',
            use: 'required',
          },
        ],
        childrens: [
          {
            name: 'ModalidadAcreditacion',
            minOccurs: 1,
            maxOccurs: '1',
            compositor: 'choice',
            contentModel: 'default',
            items: [],
            childrens: [
              {
                name: 'PorTesis',
                minOccurs: 1,
                maxOccurs: '1',
                compositor: 'sequence',
                contentModel: 'default',
                items: [
                  {
                    itemName: 'tituloTesis',
                    nodeKind: 'ELEMENT',
                    type: 'string',
                    minOccurs: 1,
                    maxOccurs: '1',
                    minLength: 5,
                    maxLength: 300,
                  },
                  {
                    itemName: 'directorTesis',
                    nodeKind: 'ELEMENT',
                    type: 'string',
                    minOccurs: 1,
                    maxOccurs: '1',
                  },
                ],
                childrens: [],
              },
              {
                name: 'PorExamenCeneval',
                minOccurs: 1,
                maxOccurs: '1',
                compositor: 'sequence',
                contentModel: 'default',
                items: [
                  {
                    itemName: 'folioCeneval',
                    nodeKind: 'ELEMENT',
                    type: 'string',
                    minOccurs: 1,
                    maxOccurs: '1',
                    pattern: '^[A-Z]{3}-[0-9]{8}$',
                  },
                  {
                    itemName: 'puntuacion',
                    nodeKind: 'ELEMENT',
                    type: 'integer',
                    minOccurs: 1,
                    maxOccurs: '1',
                    minInclusive: '700',
                    maxInclusive: '1300',
                  },
                ],
                childrens: [],
              },
            ],
          },
        ],
      },
    ],
  },
];

/**
 * Generates W3C XSD schema XML in the client from a draft for a live preview.
 */
export const generateXsdXmlPreview = (versionDraft) => {
  if (!versionDraft) return '<!-- No hay borrador cargado -->';

  const escapeXml = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  const lines = [];
  lines.push('<?xml version="1.0" encoding="UTF-8"?>');

  let schemaTag = `<xs:schema xmlns:xs="http://www.w3.org/2001/XMLSchema" version="${escapeXml(versionDraft.version || '1.0.0')}"`;
  if (versionDraft.targetNamespace && String(versionDraft.targetNamespace).trim()) {
    const ns = String(versionDraft.targetNamespace).trim();
    schemaTag += `\n  targetNamespace="${escapeXml(ns)}"`;
    schemaTag += `\n  xmlns="${escapeXml(ns)}"`;
  }
  schemaTag += `\n  elementFormDefault="${versionDraft.elementFormDefault || 'unqualified'}"`;
  schemaTag += `\n  attributeFormDefault="${versionDraft.attributeFormDefault || 'unqualified'}">`;
  lines.push(schemaTag);
  lines.push('');

  const renderFacets = (field, indent) => {
    if (field.length != null) lines.push(`${indent}<xs:length value="${field.length}" />`);
    if (field.minLength != null) lines.push(`${indent}<xs:minLength value="${field.minLength}" />`);
    if (field.maxLength != null) lines.push(`${indent}<xs:maxLength value="${field.maxLength}" />`);
    if (field.pattern) lines.push(`${indent}<xs:pattern value="${escapeXml(field.pattern)}" />`);
    if (Array.isArray(field.enumValues)) {
      field.enumValues.forEach((val) => {
        if (val) lines.push(`${indent}<xs:enumeration value="${escapeXml(val)}" />`);
      });
    }
    if (field.minInclusive) lines.push(`${indent}<xs:minInclusive value="${escapeXml(field.minInclusive)}" />`);
    if (field.maxInclusive) lines.push(`${indent}<xs:maxInclusive value="${escapeXml(field.maxInclusive)}" />`);
    if (field.minExclusive) lines.push(`${indent}<xs:minExclusive value="${escapeXml(field.minExclusive)}" />`);
    if (field.maxExclusive) lines.push(`${indent}<xs:maxExclusive value="${escapeXml(field.maxExclusive)}" />`);
    if (field.totalDigits != null) lines.push(`${indent}<xs:totalDigits value="${field.totalDigits}" />`);
    if (field.fractionDigits != null) lines.push(`${indent}<xs:fractionDigits value="${field.fractionDigits}" />`);
    if (field.whiteSpace) lines.push(`${indent}<xs:whiteSpace value="${escapeXml(field.whiteSpace)}" />`);
  };

  const renderField = (field, indent) => {
    const isAttr = field.nodeKind === 'ATTRIBUTE';
    const hasFacets = Boolean(
      field.minLength != null ||
      field.maxLength != null ||
      field.length != null ||
      field.pattern ||
      (Array.isArray(field.enumValues) && field.enumValues.length > 0) ||
      field.minInclusive ||
      field.maxInclusive ||
      field.minExclusive ||
      field.maxExclusive ||
      field.totalDigits != null ||
      field.fractionDigits != null ||
      field.whiteSpace
    );

    const typeStr = field.type ? `xs:${field.type}` : 'xs:string';

    if (isAttr) {
      let attrTag = `${indent}<xs:attribute name="${escapeXml(field.itemName || 'atributo')}"`;
      if (!hasFacets) {
        attrTag += ` type="${typeStr}"`;
      }
      if (field.use && field.use !== 'optional') {
        attrTag += ` use="${field.use}"`;
      }
      if (field.defaultValue) {
        attrTag += ` default="${escapeXml(field.defaultValue)}"`;
      }
      if (field.fixedValue) {
        attrTag += ` fixed="${escapeXml(field.fixedValue)}"`;
      }

      if (hasFacets) {
        lines.push(`${attrTag}>`);
        lines.push(`${indent}  <xs:simpleType>`);
        lines.push(`${indent}    <xs:restriction base="${typeStr}">`);
        renderFacets(field, `${indent}      `);
        lines.push(`${indent}    </xs:restriction>`);
        lines.push(`${indent}  </xs:simpleType>`);
        lines.push(`${indent}</xs:attribute>`);
      } else {
        attrTag += ' />';
        lines.push(attrTag);
      }
    } else {
      let elemTag = `${indent}<xs:element name="${escapeXml(field.itemName || 'elemento')}"`;
      if (!hasFacets) {
        elemTag += ` type="${typeStr}"`;
      }
      if (field.minOccurs != null && Number(field.minOccurs) !== 1) {
        elemTag += ` minOccurs="${field.minOccurs}"`;
      }
      if (field.maxOccurs != null && String(field.maxOccurs) !== '1') {
        elemTag += ` maxOccurs="${field.maxOccurs}"`;
      }
      if (field.nillable) {
        elemTag += ' nillable="true"';
      }

      if (hasFacets) {
        lines.push(`${elemTag}>`);
        lines.push(`${indent}  <xs:simpleType>`);
        lines.push(`${indent}    <xs:restriction base="${typeStr}">`);
        renderFacets(field, `${indent}      `);
        lines.push(`${indent}    </xs:restriction>`);
        lines.push(`${indent}  </xs:simpleType>`);
        lines.push(`${indent}</xs:element>`);
      } else {
        elemTag += ' />';
        lines.push(elemTag);
      }
    }
  };

  const renderNode = (node, indent) => {
    let elemTag = `${indent}<xs:element name="${escapeXml(node.name || 'Nodo')}"`;
    if (node.minOccurs != null && Number(node.minOccurs) !== 1) {
      elemTag += ` minOccurs="${node.minOccurs}"`;
    }
    if (node.maxOccurs != null && String(node.maxOccurs) !== '1') {
      elemTag += ` maxOccurs="${node.maxOccurs}"`;
    }
    if (node.nillable) {
      elemTag += ' nillable="true"';
    }
    if (node.abstract) {
      elemTag += ' abstract="true"';
    }
    lines.push(`${elemTag}>`);

    lines.push(`${indent}  <xs:complexType>`);

    const elements = (node.items || []).filter((i) => i.nodeKind !== 'ATTRIBUTE');
    const attributes = (node.items || []).filter((i) => i.nodeKind === 'ATTRIBUTE');
    const childrens = Array.isArray(node.childrens) ? node.childrens : (Array.isArray(node.children) ? node.children : []);
    const compositor = node.compositor || 'sequence';

    if (elements.length > 0 || childrens.length > 0 || node.hasWildcardElement) {
      lines.push(`${indent}    <xs:${compositor}>`);
      elements.forEach((f) => renderField(f, `${indent}      `));
      childrens.forEach((child) => renderNode(child, `${indent}      `));
      if (node.hasWildcardElement) {
        lines.push(`${indent}      <xs:any minOccurs="0" maxOccurs="unbounded" processContents="${node.wildcardElementProcessContents || 'lax'}" />`);
      }
      lines.push(`${indent}    </xs:${compositor}>`);
    }

    attributes.forEach((a) => renderField(a, `${indent}    `));

    if (node.hasWildcardAttribute) {
      lines.push(`${indent}    <xs:anyAttribute processContents="${node.wildcardAttributeProcessContents || 'lax'}" />`);
    }

    lines.push(`${indent}  </xs:complexType>`);

    if (Array.isArray(node.constraints) && node.constraints.length > 0) {
      node.constraints.forEach((c) => {
        const cKind = c.kind || 'unique';
        let cTag = `${indent}  <xs:${cKind} name="${escapeXml(c.name || 'constraint')}"`;
        if (cKind === 'keyref' && c.refer) {
          cTag += ` refer="${escapeXml(c.refer)}"`;
        }
        lines.push(`${cTag}>`);
        lines.push(`${indent}    <xs:selector xpath="${escapeXml(c.selector || '.')}" />`);
        (c.fields || []).forEach((f) => {
          lines.push(`${indent}    <xs:field xpath="${escapeXml(f)}" />`);
        });
        lines.push(`${indent}  </xs:${cKind}>`);
      });
    }

    lines.push(`${indent}</xs:element>`);
  };

  (versionDraft.structure || []).forEach((rootNode) => {
    renderNode(rootNode, '  ');
  });

  lines.push('</xs:schema>');
  return lines.join('\n');
};